import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

const Hero = () => {
  const { t, language } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-primary-950">
      {/* Gradient layers */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-950 via-primary-900 to-primary-800" />
      <div className="absolute inset-0 bg-gradient-to-tr from-secondary-500/5 via-transparent to-primary-500/10" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,184,204,1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,184,204,1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Glow blobs */}
      <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-primary-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/4 w-56 h-56 bg-secondary-500/8 rounded-full blur-3xl pointer-events-none" />

      {/* Real project photo — solar-powered mosque in Ma'an */}
      <div className={`absolute top-1/2 -translate-y-1/2 w-[44%] max-w-xl pointer-events-none hidden lg:block ${language === 'ar' ? 'left-0' : 'right-0'}`}>
        <div className="relative mx-10 rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10">
          <img
            src="/images/maan-solar-mosque.jpg"
            alt="Solar panels installed on a mosque roof in Ma'an"
            width="800"
            height="440"
            className="w-full h-[440px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/50 via-transparent to-primary-950/10" />
          <div className={`absolute inset-y-0 w-28 ${
            language === 'ar'
              ? 'right-0 bg-gradient-to-l from-primary-950 to-transparent'
              : 'left-0 bg-gradient-to-r from-primary-950 to-transparent'
          }`} />
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 relative z-10 pt-24 pb-16">
        <div className="max-w-2xl">

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/15 border border-primary-500/25 mb-7 animate-fadeIn">
            <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse" />
            <span className="text-primary-300 text-sm font-medium">{t('hero.badge')}</span>
          </div>

          {/* Headline */}
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fadeIn"
            style={{ animationDelay: '80ms' }}
          >
            {t('hero.title')}
          </h1>

          {/* Subtitle */}
          <p
            className="text-lg text-gray-300 leading-relaxed mb-4 animate-fadeIn"
            style={{ animationDelay: '160ms' }}
          >
            {t('hero.subtitle')}
          </p>

          {/* Tagline */}
          <p
            className="text-primary-300 font-medium italic mb-10 animate-fadeIn"
            style={{ animationDelay: '200ms' }}
          >
            {t('hero.tagline')}
          </p>

          {/* CTA buttons */}
          <div className="flex flex-wrap gap-4 animate-fadeIn" style={{ animationDelay: '240ms' }}>
            <Link
              to="/services"
              className="px-8 py-4 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-400 transition-all duration-300 shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50 hover:-translate-y-0.5"
            >
              {t('hero.exploreServices')}
            </Link>
            <Link
              to="/get-quote"
              className="px-8 py-4 border-2 border-white/25 text-white rounded-xl font-semibold hover:bg-white/10 hover:border-white/50 transition-all duration-300 hover:-translate-y-0.5"
            >
              {t('hero.getQuote')}
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-gray-500 animate-bounce">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
